import React, { useEffect, useRef, useState } from "react";
import css from "@emotion/css";
import styled from "@emotion/styled";
import Hammer from "hammerjs";

import ValueAdjuster from "./ValueAdjuster";
import PlayButton from "./PlayButton";
import useWindowSize from "../../../utils/useWindowSize";
import { useRecoilState } from "recoil";
import keys, { Flat, Minor } from "../keys";

// TODO: move the recoil objects under NotePlayer folder after refactoring
import baseKeyState from "../../../recoil/baseKeyState";
import speedState from "../../../recoil/speedState";
import chordSizeState from "../../../recoil/chordSizeState";
import isNotePlayerLoadingState from "../../../recoil/isNotePlayerLoadingState";

import SpeedIcon from "../../../assets/icons/speed.svg";
import KeyIcon from "../../../assets/icons/key.svg";
import ChordLyricsIcon from "../../../assets/icons/chord_lyrics.svg";
import CloseIcon from "../../../assets/icons/close.svg";
import ResetIcon from "../../../assets/icons/reset.png";
import MetronomeButton from "./MetronomeButton";
import { PlaylistRow } from "../../PlaylistModal";
import { getPlaylist, savePlaylist, makeNewPlaylist } from "../../../utils/playlist";
import { IPlaylist } from "../../../types";
import auth from "../../../utils/auth";
import { useHistory } from "react-router";
import Modal from "../../Modal";
import Input from "../../Input";
import { track } from "../../../utils/track";
interface IControllerWrapper {
  isControllerHidden?: boolean;
}

interface IController extends IControllerWrapper {
  info: any;
  personal?: boolean; // 내 악보: 연주목록 담기 숨김
  isPlaying?: boolean;
  onClickToggleController?: () => void;
  onClickPlayButton: () => void;
  hideProgress: boolean;
  enableMetronome?: boolean;
  onClickMetronome: () => void;
  onClickReset: () => void;
}

const Controller: React.FC<IController> = ({
  personal = false,
  isPlaying = false,
  isControllerHidden = false,
  onClickToggleController,
  onClickPlayButton,
  hideProgress,
  info,
  enableMetronome,
  onClickMetronome,
  onClickReset,
}) => {
  const [speed, setSpeed] = useRecoilState(speedState);
  const [baseKey, setBaseKey] = useRecoilState(baseKeyState);
  const [chordSize, setChordSize] = useRecoilState(chordSizeState);
  const [playlists, setPlaylists] = useState<Array<IPlaylist>>([]);
  const [isOpenPlaylistModal, setIsOpenPlaylistModal] = useState(false);
  const PlayListIcon = require(`../../../assets/songrow-icon-1.svg`).default;
  const history = useHistory();
  const [isNewPlaylist, setIsNewPlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState<string>('');

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isNotePlayerLoading, setIsNotePlayerLoading] = useRecoilState(
    isNotePlayerLoadingState
  );

  const { width: screenWidth = 1980 } = useWindowSize();
  const isSmallScreen = screenWidth <= 840;

  // 컨트롤러 상단 흰 막대: 누르거나 아래로 끌어내리면 화면 밖으로 완전히 내려가고,
  // 내려간 뒤에는 아래 가운데 작은 손잡이를 누르거나 위로 밀면 다시 올라온다.
  const [dragY, setDragY] = useState<number | null>(null);
  const dragStart = useRef<{ y: number; moved: boolean } | null>(null);
  const DRAG_HIDE_PX = 40;

  const onHandleTouchStart = (e: React.TouchEvent) => {
    dragStart.current = { y: e.touches[0].clientY, moved: false };
  };
  const onHandleTouchMove = (e: React.TouchEvent) => {
    if (!dragStart.current) return;
    const dy = e.touches[0].clientY - dragStart.current.y;
    if (Math.abs(dy) > 6) dragStart.current.moved = true;
    setDragY(Math.max(0, dy));
  };
  const onHandleTouchEnd = (e: React.TouchEvent) => {
    const start = dragStart.current;
    dragStart.current = null;
    const dy = dragY || 0;
    setDragY(null);
    if (!start) return;
    if (start.moved) {
      e.preventDefault(); // 끌기였으면 뒤따르는 click(토글)을 막는다
      if (dy > DRAG_HIDE_PX && onClickToggleController) onClickToggleController();
    }
  };

  const tabStart = useRef<number | null>(null);
  const onTabTouchStart = (e: React.TouchEvent) => {
    tabStart.current = e.touches[0].clientY;
  };
  const onTabTouchEnd = (e: React.TouchEvent) => {
    const start = tabStart.current;
    tabStart.current = null;
    if (start === null) return;
    const dy = e.changedTouches[0].clientY - start;
    if (dy < -20) {
      e.preventDefault();
      if (onClickToggleController) onClickToggleController();
    }
  };

  const setSpeedPlus = () => {
    track("score:speed");
    setSpeed(Number(parseFloat((speed + 0.1).toString()).toPrecision(12)));
  };

  const setSpeedMinus = () => {
    if (speed - 0.1 > 0) {
      setSpeed(Number(parseFloat((speed - 0.1).toString()).toPrecision(12)));
    }
  };

  const updateWindowDimensions = () => {
    const width = window.innerWidth;
    if (width <= 480) {
      setChordSize(2);
    } else {
      setChordSize(1);
    }
  };

  const refChordsize = useRef<number>(chordSize);

  useEffect(() => {
    refChordsize.current = chordSize;
  }, [chordSize]);

  const handlePinAndPan = () => {
    const el = document.getElementById('note-wrap') as HTMLElement;
    var hammer = new Hammer(el, {
      domEvents: true,
      enable: true,
    });

    hammer.get('pinch').set({
      enable: true,
    });
    el.style.touchAction = 'unset';
    let countPinchin = 0;
    let countPinchout = 0;

    hammer.on('pinchin', (e) => {
      countPinchin += 1;
    })

    hammer.on('pinchout', (e) => {
      countPinchout += 1;
    })

    hammer.on('pinchcancel', (e) => {
      console.log(countPinchin - countPinchout);
      if (countPinchin > countPinchout) {
        if (refChordsize.current - 0.5 >= 1) {
          setChordSize((prevSize) => prevSize - 0.5);
        }
      }
      if (countPinchin < countPinchout) {
        if (refChordsize.current + 0.5 <= 4) {
          setChordSize((prevSize) => prevSize + 0.5);
        }
      }
      countPinchin = 0;
      countPinchout = 0;
    })
  }


  useEffect(() => {
    updateWindowDimensions();
    window.addEventListener("resize", updateWindowDimensions);
    handlePinAndPan();

    return () => window.removeEventListener("resize", updateWindowDimensions)
  }, []);

  useEffect(() => {
    if (isOpenPlaylistModal) {
      getPlaylist().then((playlists) => setPlaylists(playlists));
    }
  }, [isOpenPlaylistModal]);

  const onClickNextBaseKey = () => {
    track("score:key");
    const nextIndex = baseKey + 1;
    setBaseKey(nextIndex);
  };

  const onClickPrevBaseKey = () => {
    track("score:key");
    const prevIndex = baseKey - 1;

    if (info.original_key.replace(/♭/g, "ᵇ").indexOf("ᵇ") >= 0) {
      setBaseKey(
        prevIndex < 0
          ? (Flat.length - Math.abs(prevIndex)) % Flat.length
          : prevIndex
      );
    } else if (info.original_key.indexOf("m") >= 0) {
      setBaseKey(
        prevIndex < 0
          ? (Flat.length - Math.abs(prevIndex)) % Flat.length
          : prevIndex
      );
    } else {
      setBaseKey(
        prevIndex < 0
          ? (keys.length - Math.abs(prevIndex)) % keys.length
          : prevIndex
      );
    }
  };

  const onClickPrevChordSize = () => {
    setIsNotePlayerLoading(true);
    if (chordSize + 0.5 <= 4) {
      setChordSize((prevSize) => prevSize + 0.5);
    }
    setTimeout(() => {
      setIsNotePlayerLoading(false);
    }, 500);
  };

  const onClickNextChordSize = () => {
    setIsNotePlayerLoading(true);
    if (chordSize - 0.5 >= 1) {
      setChordSize((prevSize) => prevSize - 0.5);
    }
    setTimeout(() => {
      setIsNotePlayerLoading(false);
    }, 500);
  };

  return (
    <>
      {isControllerHidden && (
        <ShowControllerTab
          aria-label="컨트롤러 열기"
          onClick={onClickToggleController}
          onTouchStart={onTabTouchStart}
          onTouchEnd={onTabTouchEnd}
        >
          <span />
        </ShowControllerTab>
      )}
      <ControllerWrapper
        isControllerHidden={isControllerHidden}
        style={dragY !== null ? { transform: `translateY(${dragY}px)`, transition: 'none' } : undefined}
      >
        <ControllerContainer isControllerHidden={isControllerHidden}>
          {/* {isControllerHidden ? (
            <OpenIconWrapper>
              <CloseIcon />
            </OpenIconWrapper>
          ) : ( */}
            <ControllerContent>
              <ShowHideControllerPanel
                aria-label="컨트롤러 내리기"
                onClick={onClickToggleController}
                onTouchStart={onHandleTouchStart}
                onTouchMove={onHandleTouchMove}
                onTouchEnd={onHandleTouchEnd}
              >
                <span />
              </ShowHideControllerPanel>
              <SmallScreenContainer>
                <PlayButtonWrapper>
                  <PlayButton isPlaying={isPlaying} onClick={onClickPlayButton} />
                  {/* <CloseControllerButton onClick={onClickToggleController}>
                    <CloseIcon />
                  </CloseControllerButton> */}
                  <ResetIconButton onClick={onClickReset}/>
                </PlayButtonWrapper>
                <AdjusterList>
                  {!hideProgress && (
                    <ValueAdjuster
                      isSmallScreen={isSmallScreen}
                      field="Speed"
                      value={`${speed}x`}
                      onClickPlus={() => setSpeedPlus()}
                      onClickMinus={() => setSpeedMinus()}
                      icon={<SpeedIcon />}
                    />
                  )}
                  <ValueAdjuster
                    isSmallScreen={isSmallScreen}
                    field="Key"
                    value={
                      info.original_key.replace(/♭/gi, "ᵇ").indexOf("ᵇ") >= 0
                        ? Flat[
                        (baseKey +
                          Flat.indexOf(
                            info.original_key
                              .replace(/♭/gi, "ᵇ")
                              .replace("#", "<sup>#</sup>")
                          )) %
                        Flat.length
                        ]
                        : info.original_key.indexOf("m") >= 0
                          ? Minor[
                          (baseKey +
                            Minor.indexOf(
                              info.original_key.replace("#", "<sup>#</sup>")
                            )) %
                          Minor.length
                          ]
                          : // keys.indexOf(
                          //     info.original_key.replace("#", "<sup>#</sup>")
                          //   ) < 0
                          // ? keys[baseKey % keys.length]
                          // :
                          keys[
                          (baseKey +
                            keys.indexOf(
                              info.original_key.replace("#", "<sup>#</sup>")
                            )) %
                          keys.length
                          ]
                    }
                    onClickPlus={onClickNextBaseKey}
                    onClickMinus={onClickPrevBaseKey}
                    icon={<KeyIcon />}
                  />
                  <ValueAdjuster
                    isSmallScreen={isSmallScreen}
                    field="Size"
                    value={`${chordSize}x`}
                    onClickPlus={onClickPrevChordSize}
                    onClickMinus={onClickNextChordSize}
                    icon={<ChordLyricsIcon />}
                    style={{ marginRight: 2 }}
                  />
                </AdjusterList>
                <MetronomeContainer>
                  <MetronomeButton enable={enableMetronome} onClick={onClickMetronome} />
                </MetronomeContainer>
                <PlaylistContainerWrap>
                  <FieldText
                    dangerouslySetInnerHTML={{
                      __html: 'Playlist',
                    }}
                  />
                  {!personal && <PlaylistButton
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      auth.getToken()
                        ? setIsOpenPlaylistModal(true)
                        : history.push('/playlist');
                    }}
                  >
                    <PlayListIcon />
                  </PlaylistButton>}
                </PlaylistContainerWrap>
              </SmallScreenContainer>
            </ControllerContent>
          {/* )} */}
        </ControllerContainer>
      </ControllerWrapper>
      {isOpenPlaylistModal && (
        <Modal
          open={isOpenPlaylistModal}
          onClose={() => setIsOpenPlaylistModal(false)}
          title={!isNewPlaylist ? '연주목록에 담기' : '새 연주목록'}
          firstButtonLabel={
            !isNewPlaylist ? '새 연주목록 만들기' : '생성하기'
          }
          onClickFirstButton={
            isNewPlaylist
              ? async () => {
                await makeNewPlaylist(newPlaylistName);
                const playlists = await getPlaylist();
                setPlaylists(playlists);
                setIsNewPlaylist(false);
              }
              : () => setIsNewPlaylist(true)
          }
          secondButtonLabel="취소"
          onClickSecondButton={() =>
            isNewPlaylist
              ? setIsNewPlaylist(false)
              : setIsOpenPlaylistModal(false)
          }
          content={
            <>
              {!isNewPlaylist ? (
                <PlaylistWrap>
                  {playlists.length === 0 && (<div style={{ padding: '16px 0', textAlign: 'center', color: '#9aa0a6', fontSize: 14 }}>아직 연주목록이 없어요.<br />아래 '새 연주목록 만들기'를 눌러 주세요.</div>)}
                  {playlists.map((playlist) => (
                    <PlaylistRow
                      key={`playlist-${playlist.id}`}
                      id={playlist.id}
                      title={playlist.name}
                      count={playlist.total_count}
                      onClick={async (playlistId: number) => {
                        await savePlaylist(playlistId, info.id.toString());
                        const playlists = await getPlaylist();
                        setPlaylists(playlists);
                        setIsOpenPlaylistModal(false);
                      }}
                    />
                  ))}
                </PlaylistWrap>
              ) : (
                <StyledInput
                  icon="edit"
                  placeholder="연주목록 이름을 입력해주세요"
                  value={newPlaylistName}
                  onChange={(e) => setNewPlaylistName(e.target.value)}
                />
              )}
            </>
          }
        />
      )}
    </>
  );
};

export default Controller;

const ControllerWrapper = styled.div<IControllerWrapper>`
  width: 100%;
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  bottom: constant(safe-area-inset-bottom);
  bottom: env(safe-area-inset-bottom);
  display: flex;
  transition: transform 0.25s ease;
  cursor: pointer;
  /* 화면 가로 전체를 덮는 투명한 틀이 손가락 스크롤을 막지 않게 (가로 모드에서 컨트롤러 양옆) */
  pointer-events: none;

  @media screen and (max-width: 360px) {
    position: fixed;
    z-index: 999;
  }

  ${({ isControllerHidden = false }) =>
    isControllerHidden &&
    css`
      transform: translateY(calc(100% + 40px));
      pointer-events: none;
    `};
`;

// 컨트롤러를 내린 뒤 다시 여는 작은 손잡이 (악보를 가리지 않게 작고 반투명하게)
const ShowControllerTab = styled.button`
  position: fixed;
  left: 50%;
  bottom: 0;
  bottom: env(safe-area-inset-bottom);
  transform: translateX(-50%);
  z-index: 999;
  width: 96px;
  height: 28px;
  padding: 0;
  border: 0;
  border-radius: 14px 14px 0 0;
  background: rgba(10, 132, 255, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  outline: 0;

  & > span {
    display: block;
    width: 40px;
    height: 5px;
    border-radius: 3px;
    background: white;
  }
`;

interface IButton {
  checked?: boolean;
  isOpacityDisabled?: boolean;
}
const Button = styled.button<IButton>`
  border: solid 1px rgba(255,255,255,0.41);
  padding: 0;
  outline: 0;
  background: none;

  display: flex;
  justify-content: center;
  align-items: center;
  width: 50px;
  height: 50px;
  min-width: 37px;
  min-height: 37px;
  background-color: rgba(74, 74, 75, 0.05);
  border-radius: 6px;
  margin-left: auto;
  &:last-of-type {
    margin-right: 0;
  }

  @media screen and (max-width: 840px) {
    width: 37px;
    height: 37px;
  }

  html[color-mode='dark'] & {
    background-color: rgba(216, 216, 216, 0.09);

    & path {
      /* opacity: ${({ isOpacityDisabled }) =>
    isOpacityDisabled ? 1 : 0.4} !important; */
      fill: #ffffff !important;
    }
  }

  ${({ checked }) =>
    checked &&
    css`
      background-color: rgba(10, 98, 186, 0.69);
      html[color-mode='dark'] & {
        background-color: rgba(10, 10, 11, 0.35);
        & path {
          fill: white !important;
          opacity: 0.53;
        }
      }
    `}
`;

const StyledInput = styled(Input)`
  margin-top: 36px;

  html[color-mode='dark'] & {
    background-color: #2e3137;
  }
`;

const ControllerContainer = styled.div<IControllerWrapper>`
  pointer-events: auto;
  background-color: #0a84ff;
  border-radius: 14px;
  box-shadow: 0 2px 7px 0 rgba(139, 139, 139, 0.78);
  margin: 0 auto;
  height: 150px;
  transition: height 0.5s ease;

  @media screen and (max-width: 840px) {
    height: 131px;
  }

`;

const ControllerContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 23px 50px;
  padding-bottom: 20px;
  padding-top: 0px;
  align-items: center;
  position: relative;

  @media screen and (max-width: 840px) {
    padding: 16px 15px;
    padding-top: 0px;
  }
`;

const ShowHideControllerPanel = styled.div`
  width: 70%;
  /* 누르기 쉽게 위아래로 터치 영역을 넓히되, 차지하는 높이는 예전(막대 13px + 아래 10px)과 같게 */
  padding: 5px 0;
  margin: -5px 0 5px;
  touch-action: none;
  cursor: pointer;

  & > span {
    display: block;
    height: 13px;
    background-color: white;
    border-radius: 10px;
  }
`
const ResetIconButton = styled.div`
  background-image: url("${ResetIcon}");
  background-size: cover;
  height: 50px;
  width: 50px;
  cursor: pointer;

  @media screen and (max-width: 840px) {
    height: 43px;
    width: 43px;
  }
`
const SmallScreenContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const AdjusterList = styled.div`
  display: flex;
  align-items: flex-end;
  margin-left: 50px;

  @media screen and (max-width: 840px) {
    // margin-left: 16px;
    margin-left: 0px;
  }
`;

const PlayButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
`;

const MetronomeContainer = styled.div`
margin-left: 50px;

@media screen and (max-width: 840px) {
  margin-left: 0px;
}
`;

const CloseControllerButton = styled.div`
  margin-top: 12px;
  cursor: pointer;

  & > svg {
    width: 23px;
    height: 11px;
  }

  @media screen and (max-width: 840px) {
    margin-top: 10px;
  }
`;

const OpenIconWrapper = styled.div`
  & > svg {
    transform: rotate(180deg);
    height: 11px;
    width: 23px;
  }
`;

const FieldText = styled.span`
  margin-bottom: 11px;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.23;
  color: #ffffff;
  height: ${13 * 1.23}px;
  text-align: center;

  @media screen and (max-width: 840px) {
    height: unset;
  }
`;

const PlaylistContainerWrap = styled.span`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-left: 50px;
  @media screen and (max-width: 840px) {
    margin-left: 0px;
  }
`;

export const PlaylistWrap = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: 224px;
  border-radius: 5px;
  background-color: #f1f1f1;
  padding: 15px;
  overflow-x: hidden;
  overflow-y: scroll;

  html[color-mode='dark'] & {
    background-color: rgba(216, 216, 216, 0.05);
  }
`;

const PlaylistButton = styled(Button)`
  @media screen and (max-width: 840px) {
    margin: 14px 0px;
  }
`